import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  Wallet, Percent, Star, RefreshCw, ShoppingCart, TrendingUp,
  ShieldCheck, PackageOpen, ChevronDown, Menu, X, ArrowUpRight,
  Users, BarChart3,
} from "lucide-react";
import { PablooLogoWithText } from "../../assets/assets";
import { sendSlackMessage } from "../../models/notifications";
import { readCart, readPurchasedCart } from "../../modules/checkoutHelpers";
import { ShoppingCartIcon } from "@heroicons/react/20/solid";
import { isSessionActive } from "../../modules/sessionCookie";
import PromotionalBanner from "./promotionalBanner";

// ─── nav data ─────────────────────────────────────────────────────────────────

const PRODUCTS = [
  {
    icon:  <Wallet size={18} strokeWidth={1.75} />,
    label: "Store Credit",
    desc:  "Issue and manage store currency that shoppers actually spend.",
    href:  "/products",
  },
  {
    icon:  <Percent size={18} strokeWidth={1.75} />,
    label: "Cashback",
    desc:  "Cash rewards at checkout that bring customers back.",
    href:  "/products/cashback",
  },
  {
    icon:  <Star size={18} strokeWidth={1.75} />,
    label: "Memberships",
    desc:  "Subscription loyalty tiers that compound retention.",
    href:  "/products/memberships",
  },
  {
    icon:  <RefreshCw size={18} strokeWidth={1.75} />,
    label: "Store Credit Refunds",
    desc:  "Turn every return into a retained customer.",
    href:  "/products/store-credit-refunds",
  },
];

const SOLUTIONS = [
  {
    icon:  <ShoppingCart size={18} strokeWidth={1.75} />,
    label: "Cart Abandonment",
    desc:  "Credit incentives that finish the checkout.",
    href:  "/solutions/shopify-cart-abandonment-solutions",
  },
  {
    icon:  <TrendingUp size={18} strokeWidth={1.75} />,
    label: "Cashback vs. Discounts",
    desc:  "Why issuing credit beats cutting prices.",
    href:  "/solutions/shopify/cashback/shopify-cashback-vs-discounts",
  },
  {
    icon:  <ShieldCheck size={18} strokeWidth={1.75} />,
    label: "No More Chargebacks",
    desc:  "Stop disputes before they reach your processor.",
    href:  "/solutions/shopify/no-more-chargebacks",
  },
  {
    icon:  <PackageOpen size={18} strokeWidth={1.75} />,
    label: "Inventory Liquidation",
    desc:  "Move dead stock with credit campaigns instead of markdowns.",
    href:  "/solutions/shopify/inventory-liquidation",
  },
  {
    icon:  <Users size={18} strokeWidth={1.75} />,
    label: "Customer Retention",
    desc:  "Keep your best customers from going quiet.",
    href:  "/solutions",
  },
  {
    icon:  <BarChart3 size={18} strokeWidth={1.75} />,
    label: "Cashback Programs",
    desc:  "End-to-end cashback architecture for Shopify brands.",
    href:  "/solutions/cashback",
  },
];

// ─── constants ────────────────────────────────────────────────────────────────

const BAR_H = 60;
const EASE  = [0.22, 1, 0.36, 1];

// ─── MobileNav ────────────────────────────────────────────────────────────────

export default function MobileNav({ shouldShowPremiumBanner, theme }) {
  const navigate       = useNavigate();
  const location       = useLocation();
  const [isSignedIn] = useState(() => isSessionActive());

  const dark = theme === "managed";
  const GOLD = "#c9a85c";
  const N = dark ? {
    barBg:      (scrolled) => scrolled ? "rgba(8,8,10,0.97)" : "rgba(8,8,10,0.85)",
    border:     (scrolled) => scrolled ? "rgba(201,168,92,0.14)" : "transparent",
    shadow:     (scrolled) => scrolled ? "0 4px 32px rgba(0,0,0,0.5)" : "none",
    iconColor:  "#c8bfb0",
    drawerBg:   "#0d0d10",
    drawerBorder: "rgba(255,255,255,0.06)",
    linkColor:  "#c8bfb0",
    accent:     GOLD,
    cta:        GOLD,
    ctaText:    "#08080a",
    ctaHover:   "#b8954a",
    logoFilter: "brightness(0) invert(1)",
  } : {
    barBg:      (scrolled) => scrolled ? "rgba(255,255,255,0.95)" : "#ffffff",
    border:     (scrolled) => scrolled ? "rgba(0,0,0,0.07)" : "transparent",
    shadow:     (scrolled) => scrolled ? "0 4px 24px rgba(0,0,0,0.07)" : "none",
    iconColor:  "#1a1a1a",
    drawerBg:   "#ffffff",
    drawerBorder: "rgba(0,0,0,0.06)",
    linkColor:  "#1a1a1a",
    accent:     "#16a34a",
    cta:        "#16a34a",
    ctaText:    "#ffffff",
    ctaHover:   "#15803d",
    logoFilter: "none",
  };

  const [open,        setOpen]        = useState(false);
  const [expanded,    setExpanded]    = useState(null);
  const [scrolled,    setScrolled]    = useState(false);
  const [hidden,      setHidden]      = useState(false);
  const [cartVisible, setCartVisible] = useState(false);
  const [hideBanner,  setHideBanner]  = useState(false);

  const lastScrollY = useRef(0);

  // ── scroll: frosted + smart hide ─────────────────────────────────────────
  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 12);
      if (y < 80) {
        setHidden(false);
      } else if (y > lastScrollY.current + 6) {
        setHidden(true);
        setOpen(false);
      } else if (y < lastScrollY.current - 6) {
        setHidden(false);
      }
      lastScrollY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // ── cart ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    readCart((c) => {
      const items = c?.productLineItems || [];
      setCartVisible(items.length > 0);
      if (items.length > 0) setHideBanner(true);
    });
    readPurchasedCart((c) => {
      if ((c?.productLineItems || []).length > 0) setHideBanner(true);
    });
  }, []);

  // ── close drawer on route change ─────────────────────────────────────────
  useEffect(() => { setOpen(false); setExpanded(null); }, [location.pathname]);

  // ── helpers ───────────────────────────────────────────────────────────────
  const isActive = (href) => href && location.pathname === href;
  const toggle   = (key) => setExpanded((e) => (e === key ? null : key));

  const handleStartFree = () => {
    sendSlackMessage("Someone has clicked on the 'Start for Free' from the Navigation tab.", "#website-updates");
    window.gtag("event", "conversion_button_clicked", { button: "view_shopify_listing", source: "Navigation button" });
    if (isSignedIn) {
      window.location.href = "https://app.pabloo.com";
    } else {
      window.location.href = "https://app.pabloo.com/sign-up";
    }
  };

  // ── accordion section ─────────────────────────────────────────────────────
  const Accordion = ({ id, label, items }) => (
    <div style={{ borderBottom: `1px solid ${N.drawerBorder}` }}>
      <button
        type="button"
        onClick={() => toggle(id)}
        style={{
          display:        "flex",
          justifyContent: "space-between",
          alignItems:     "center",
          width:          "100%",
          padding:        "17px 20px",
          background:     "none",
          border:         "none",
          fontSize:       "0.9375rem",
          fontWeight:     600,
          color:          N.linkColor,
          cursor:         "pointer",
          fontFamily:     "inherit",
          textAlign:      "left",
        }}
      >
        {label}
        <ChevronDown
          size={16}
          style={{
            transition: "transform 0.2s",
            transform:  expanded === id ? "rotate(180deg)" : "none",
            color:      "#9ca3af",
            flexShrink: 0,
          }}
        />
      </button>

      <AnimatePresence>
        {expanded === id && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: EASE }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "0 20px 12px" }}>
              {items.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  style={{
                    display:        "flex",
                    alignItems:     "flex-start",
                    gap:            "12px",
                    padding:        "11px 0",
                    borderBottom:   "1px solid rgba(0,0,0,0.04)",
                    textDecoration: "none",
                  }}
                >
                  <div style={{ color: N.accent, marginTop: "1px", flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: "0.875rem", fontWeight: 600, color: N.linkColor }}>{item.label}</div>
                    <div style={{ fontSize: "0.75rem", color: "#9ca3af", marginTop: "2px", lineHeight: 1.45 }}>{item.desc}</div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  // ─── render ───────────────────────────────────────────────────────────────

  return (
    <>
      {/* spacer */}
      <div style={{ height: hideBanner ? BAR_H : BAR_H + 40 }} />

      {/* ── fixed top bar ─────────────────────────────────────────────────── */}
      <div
        style={{
          position:             "fixed",
          top:                  0,
          left:                 0,
          right:                0,
          zIndex:               1000,
          transform:            hidden ? "translateY(-110%)" : "translateY(0)",
          transition:           "transform 0.35s cubic-bezier(0.22,1,0.36,1), background 0.2s, box-shadow 0.2s, border-color 0.2s",
          background:           N.barBg(scrolled),
          backdropFilter:       scrolled ? "blur(20px)" : "none",
          WebkitBackdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom:         `1px solid ${N.border(scrolled)}`,
          boxShadow:            N.shadow(scrolled),
        }}
      >
        <div
          style={{
            height:         `${BAR_H}px`,
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            padding:        "0 20px",
          }}
        >
          {/* Logo */}
          <a href="/" style={{ display: "flex", alignItems: "center" }}>
            <img src={PablooLogoWithText} alt="Pabloo" style={{ height: "22px", width: "auto", filter: N.logoFilter }} loading="lazy" />
          </a>

          {/* Right side */}
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            {cartVisible && (
              <button
                type="button"
                onClick={() => navigate("/cart")}
                style={{ background: "none", border: "none", cursor: "pointer", color: "#374151", width: "22px", height: "22px", padding: 0 }}
              >
                <ShoppingCartIcon />
              </button>
            )}

            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              style={{ background: "none", border: "none", cursor: "pointer", color: N.iconColor, padding: "4px", lineHeight: 1 }}
              aria-label={open ? "Close menu" : "Open menu"}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={open ? "x" : "menu"}
                  initial={{ opacity: 0, rotate: open ? -90 : 90 }}
                  animate={{ opacity: 1, rotate: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  {open ? <X size={24} /> : <Menu size={24} />}
                </motion.div>
              </AnimatePresence>
            </button>
          </div>
        </div>

        {!hideBanner && <PromotionalBanner shouldShowPremium={shouldShowPremiumBanner} />}
      </div>

      {/* ── drawer ────────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* backdrop */}
            <motion.div
              key="backdrop"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              style={{
                position: "fixed",
                inset:    0,
                top:      hideBanner ? BAR_H : BAR_H + 40,
                zIndex:   998,
                background: "rgba(0,0,0,0.25)",
              }}
            />

            {/* drawer panel */}
            <motion.div
              key="drawer"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25, ease: EASE }}
              style={{
                position:  "fixed",
                top:       hideBanner ? BAR_H : BAR_H + 40,
                left:      0,
                right:     0,
                zIndex:    999,
                background: N.drawerBg,
                maxHeight: `calc(100dvh - ${hideBanner ? BAR_H : BAR_H + 40}px)`,
                overflowY: "auto",
                boxShadow: "0 24px 48px rgba(0,0,0,0.14)",
              }}
            >
              {/* accordions */}
              <Accordion id="products"  label="Products"  items={PRODUCTS}  />
              <Accordion id="solutions" label="Solutions" items={SOLUTIONS} />

              {/* flat nav links */}
              {[
                { label: "Features",     href: "/features" },
                { label: "Pricing",      href: "/pricing" },
                { label: "Case Studies", href: "/case-study" },
              ].map((l) => {
                const active = isActive(l.href);
                return (
                  <div key={l.href} style={{ borderBottom: `1px solid ${N.drawerBorder}`, background: active ? (dark ? "rgba(201,168,92,0.06)" : "#f6f5ef") : "transparent" }}>
                    <Link
                      to={l.href}
                      style={{
                        display:        "block",
                        padding:        "17px 20px",
                        fontSize:       "0.9375rem",
                        fontWeight:     active ? 700 : 600,
                        color:          active ? N.accent : N.linkColor,
                        textDecoration: "none",
                      }}
                    >
                      {l.label}
                    </Link>
                  </div>
                );
              })}

              {/* CTAs */}
              <div style={{ padding: "16px 20px 28px" }}>
                {/* Managed Economy */}
                <button
                  type="button"
                  onClick={() => {
                    sendSlackMessage("Visitor clicked Managed Economy in mobile nav.", "#website-updates");
                    navigate("/bookCall");
                    setOpen(false);
                  }}
                  style={{
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "space-between",
                    width:          "100%",
                    padding:        "16px 20px",
                    borderRadius:   "14px",
                    background:     "#1a1a1a",
                    border:         "none",
                    cursor:         "pointer",
                    fontFamily:     "inherit",
                    marginBottom:   "10px",
                  }}
                >
                  <div style={{ textAlign: "left" }}>
                    <div style={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#4ade80", marginBottom: "3px" }}>
                      Managed Economy
                    </div>
                    <div style={{ fontSize: "0.9rem", fontWeight: 600, color: "#ffffff" }}>
                      Put an economist behind your brand.
                    </div>
                  </div>
                  <ArrowUpRight size={18} style={{ color: "#4ade80", flexShrink: 0, marginLeft: "12px" }} />
                </button>

                {/* Start for free */}
                <button
                  type="button"
                  onClick={() => { handleStartFree(); setOpen(false); }}
                  style={{
                    display:        "flex",
                    alignItems:     "center",
                    justifyContent: "center",
                    width:          "100%",
                    padding:        "15px 20px",
                    borderRadius:   "14px",
                    background:     N.cta,
                    border:         "none",
                    color:          N.ctaText,
                    fontSize:       "0.9375rem",
                    fontWeight:     700,
                    cursor:         "pointer",
                    fontFamily:     "inherit",
                    marginBottom:   "8px",
                  }}
                >
                  {isSignedIn ? "Go to dashboard" : "Start for free"}
                </button>

                {/* Sign in */}
                {!isSignedIn && (
                  <a
                    href="https://app.pabloo.com/sign-in"
                    onClick={() => setOpen(false)}
                    style={{
                      display:        "block",
                      width:          "100%",
                      padding:        "10px",
                      fontSize:       "0.875rem",
                      color:          "#9ca3af",
                      textAlign:      "center",
                      textDecoration: "none",
                    }}
                  >
                    Sign in
                  </a>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
